import { create } from 'zustand'

/**
 * Bid menu collapse state, remembered per browser user. Two preferences:
 * editor pages need the width, so the menu starts collapsed there unless the
 * user has expanded it on an editor page before.
 */
export const BID_MENU_COLLAPSED_KEY = 'bidcontext:bid-menu:collapsed'
export const BID_MENU_EDITOR_COLLAPSED_KEY = 'bidcontext:bid-menu:editor-collapsed'

function readFlag(key: string, fallback: boolean): boolean {
  try {
    const value = localStorage.getItem(key)
    if (value === 'true') return true
    if (value === 'false') return false
  } catch {
    // Storage unavailable (private mode): fall back to the default.
  }
  return fallback
}

function writeFlag(key: string, value: boolean) {
  try {
    localStorage.setItem(key, String(value))
  } catch {
    // Not persisted; the in-memory state still applies for this session.
  }
}

interface BidMenuState {
  collapsed: boolean
  editorCollapsed: boolean
  setCollapsed: (collapsed: boolean, onEditor: boolean) => void
  /** Re-read storage (tests, and another tab having changed it). */
  hydrate: () => void
}

export const useBidMenuStore = create<BidMenuState>((set) => ({
  collapsed: readFlag(BID_MENU_COLLAPSED_KEY, false),
  editorCollapsed: readFlag(BID_MENU_EDITOR_COLLAPSED_KEY, true),
  setCollapsed: (collapsed, onEditor) => {
    if (onEditor) {
      writeFlag(BID_MENU_EDITOR_COLLAPSED_KEY, collapsed)
      set({ editorCollapsed: collapsed })
    } else {
      writeFlag(BID_MENU_COLLAPSED_KEY, collapsed)
      set({ collapsed })
    }
  },
  hydrate: () =>
    set({
      collapsed: readFlag(BID_MENU_COLLAPSED_KEY, false),
      editorCollapsed: readFlag(BID_MENU_EDITOR_COLLAPSED_KEY, true),
    }),
}))
